import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/api";
import { useLang } from "@/context/LangContext";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Check, Sparkles, Search, MapPin } from "lucide-react";
import { FEATURE_LABELS, FEATURE_DESCRIPTIONS } from "@/lib/planFeatures";

const FEATURES = {
  basic: ["Perfil en el directorio", "Hasta 25 clientes", "Facturación con logo", "Soporte por email"],
  pro: ["Todo lo del plan Básico", "Clientes ilimitados", "Gestión de proyectos", "Empleados y control de tiempo", "Posición destacada"],
  enterprise: ["Todo lo del plan Pro", "Multi-sucursal", "Reportes avanzados", "Soporte prioritario 24/7", "Gerente de cuenta dedicado"],
};

export default function Pricing() {
  const { t, lang } = useLang();
  const { user } = useAuth();
  const navigate = useNavigate();
  const [plans, setPlans] = useState({});
  const [promo, setPromo] = useState(null);
  const [featKeys, setFeatKeys] = useState([]);
  const [featDefaults, setFeatDefaults] = useState({});
  const [featOverrides, setFeatOverrides] = useState({});

  useEffect(() => {
    api.get("/plans").then((r) => {
      setPlans(r.data);
      setFeatKeys(r.data.feature_keys || []);
      setFeatDefaults(r.data.feature_defaults || {});
      setFeatOverrides(r.data.feature_overrides || {});
    }).catch(() => {});
    api.get("/discounts/active").then((r) => setPromo(r.data && r.data.active ? r.data : null)).catch(() => {});
  }, []);

  const featOn = (plan, key) => {
    const ov = featOverrides[plan] || {};
    if (key in ov) return !!ov[key];
    return !!(featDefaults[plan] && featDefaults[plan][key]);
  };

  const choose = (key) => {
    if (!user || !user.role) return navigate("/register");
    if (user.role === "business" || user.role === "admin") return navigate("/dashboard/subscription");
    navigate("/register");
  };

  const order = ["basic", "pro", "enterprise"];

  return (
    <div className="mx-auto max-w-6xl px-6 py-16">
      <div className="text-center">
        <h1 className="font-head text-4xl font-extrabold tracking-tight text-secondary">{t("nav_pricing")}</h1>
        <p className="mx-auto mt-4 max-w-xl text-slate-600">{t("grow_sub")}</p>
        <p className="mt-2 text-sm font-semibold text-primary" data-testid="pricing-commission-note">
          Comisión de plataforma según tu plan:
          {order.filter((k) => plans[k]).map((k, i) => (
            <span key={k}>{i > 0 ? " · " : " "}{lang === "es" ? plans[k].es : plans[k].en} {plans[k].commission}%</span>
          ))}
        </p>
        {promo && (
          <div className="mx-auto mt-4 inline-flex items-center gap-2 rounded-full bg-accent/10 px-4 py-2 text-sm font-bold text-accent" data-testid="promo-banner">
            <Sparkles className="h-4 w-4" />
            {lang === "es"
              ? `Promoción ${promo.code}: ${promo.percent}% de descuento`
              : `Promo ${promo.code}: ${promo.percent}% off`}
            {promo.end_date ? (lang === "es" ? ` · hasta ${promo.end_date}` : ` · until ${promo.end_date}`) : ""}
          </div>
        )}
      </div>

      <div className="mx-auto mt-8 flex max-w-2xl items-center justify-center gap-3 rounded-sm border border-accent/30 bg-accent/5 px-5 py-4 text-center" data-testid="pricing-free-search-note">
        <Search className="h-5 w-5 shrink-0 text-accent" />
        <p className="text-sm text-slate-700">
          {lang === "es"
            ? <><span className="font-bold text-secondary">Buscar servicios es 100% gratis para los clientes.</span> Estos planes son solo para negocios que quieren aparecer y crecer en URBIS.</>
            : <><span className="font-bold text-secondary">Searching for services is 100% free for customers.</span> These plans are only for businesses that want to get listed and grow on URBIS.</>}
        </p>
      </div>

      <div className="mt-12 grid grid-cols-1 gap-6 md:grid-cols-3">
        {order.map((key) => {
          const p = plans[key];
          if (!p) return null;
          const highlight = key === "pro";
          return (
            <div key={key} className={`relative flex flex-col rounded-sm border bg-white p-8 shadow-sm ${highlight ? "border-primary shadow-lg ring-2 ring-primary" : "border-slate-200"}`} data-testid={`plan-card-${key}`}>
              {highlight && <span className="absolute -top-3 left-8 rounded-sm bg-primary px-3 py-1 text-xs font-bold uppercase tracking-wider text-white">Popular</span>}
              <h3 className="font-head text-xl font-bold text-secondary">{lang === "es" ? p.es : p.en}</h3>
              <div className="mt-4" data-testid={`plan-price-${key}`}>
                {promo ? (
                  <div className="flex flex-wrap items-baseline gap-2">
                    <span className="font-head text-4xl font-extrabold text-secondary">${(p.price * (1 - promo.percent / 100)).toFixed(2)}</span>
                    <span className="text-lg font-semibold text-slate-400 line-through">${p.price}</span>
                    <span className="text-slate-500">/mes</span>
                  </div>
                ) : (
                  <div>
                    <span className="font-head text-4xl font-extrabold text-secondary">${p.price}</span>
                    <span className="text-slate-500">/mes</span>
                  </div>
                )}
                {promo && <p className="mt-1 text-xs font-bold uppercase tracking-wide text-accent">{lang === "es" ? `Precio con ${promo.code}` : `Price with ${promo.code}`}</p>}
              </div>
              <ul className="mt-6 flex-1 space-y-3">
                {FEATURES[key].map((f) => (
                  <li key={f} className="flex items-start gap-2 text-sm text-slate-600"><Check className="mt-0.5 h-4 w-4 shrink-0 text-accent" /> {f}</li>
                ))}
              </ul>
              <Button onClick={() => choose(key)} className={`mt-8 rounded-sm ${highlight ? "bg-primary hover:bg-orange-600" : "bg-secondary hover:bg-slate-700"}`} data-testid={`plan-select-${key}`}>
                {t("grow_cta")}
              </Button>
            </div>
          );
        })}
      </div>

      {(plans.addons?.multi_location?.packages || []).some((p) => (p.monthly || p.yearly)) && (
        <div className="mt-14" data-testid="pricing-addons">
          <h2 className="text-center font-head text-2xl font-bold text-secondary">{lang === "es" ? "Complementos (Add-ons)" : "Add-ons"}</h2>
          <p className="mt-1 text-center text-sm text-slate-500">{lang === "es" ? "Disponibles para cualquier plan." : "Available for any plan."}</p>
          <div className="mx-auto mt-6 max-w-3xl rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
            <div className="flex items-center gap-2"><MapPin className="h-5 w-5 text-primary" /><h3 className="font-head text-lg font-bold text-secondary">{lang === "es" ? "Multi-Sucursal" : "Multi-location"}</h3></div>
            <p className="mt-1 text-sm text-slate-600">{lang === "es" ? "Aparece en el directorio de varias ciudades y muestra todas tus sucursales en tu perfil." : "Appear in multiple cities' directories and show all your branches on your profile."}</p>
            <div className="mt-4 grid grid-cols-2 gap-3 sm:grid-cols-4">
              {plans.addons.multi_location.packages.map((p) => (
                <div key={p.size} className="rounded-xl border border-slate-200 p-4 text-center" data-testid={`addon-price-${p.size}`}>
                  <p className="font-head text-xl font-extrabold text-secondary">{p.size}</p>
                  <p className="text-xs text-slate-400">{lang === "es" ? "sucursales" : "branches"}</p>
                  {p.monthly ? <p className="mt-2 text-sm font-bold text-primary">${p.monthly}<span className="text-xs font-normal text-slate-400">/{lang === "es" ? "mes" : "mo"}</span></p> : null}
                  {p.yearly ? <p className="text-xs text-slate-500">${p.yearly}/{lang === "es" ? "año" : "yr"}</p> : null}
                  {!p.monthly && !p.yearly ? <p className="mt-2 text-sm font-bold text-accent">{lang === "es" ? "Gratis" : "Free"}</p> : null}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      <div className="mt-16" data-testid="pricing-comparison">
        <h2 className="text-center font-head text-2xl font-bold text-secondary">{lang === "es" ? "Compara todos los servicios" : "Compare all features"}</h2>
        <div className="mt-8 overflow-x-auto">
          <table className="w-full min-w-[560px] border-collapse text-sm">
            <thead>
              <tr className="border-b-2 border-slate-200">
                <th className="p-3 text-left font-semibold text-slate-500">{lang === "es" ? "Servicio" : "Feature"}</th>
                {order.map((k) => (
                  <th key={k} className={`p-3 text-center font-head font-bold ${k === "pro" ? "text-primary" : "text-secondary"}`} data-testid={`compare-head-${k}`}>{plans[k] ? (lang === "es" ? plans[k].es : plans[k].en) : k}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {(featKeys.length ? featKeys : Object.keys(FEATURE_LABELS)).map((key) => (
                <tr key={key} className="border-b border-slate-100" data-testid={`compare-row-${key}`}>
                  <td className="p-3 text-slate-700">{FEATURE_LABELS[key] ? FEATURE_LABELS[key][lang] : key}</td>
                  {order.map((k) => (
                    <td key={k} className={`p-3 text-center ${k === "pro" ? "bg-primary/5" : ""}`} data-testid={`compare-${k}-${key}`}>
                      {featOn(k, key) ? <Check className="mx-auto h-4 w-4 text-accent" /> : <span className="text-slate-300">—</span>}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <div className="mt-12" data-testid="pricing-glossary">
          <h3 className="font-head text-xl font-bold text-secondary">{lang === "es" ? "¿Qué incluye cada función?" : "What does each feature include?"}</h3>
          <p className="mt-1 text-sm text-slate-500">{lang === "es" ? "Conoce en detalle lo que obtiene tu negocio con cada función." : "Learn in detail what your business gets with each feature."}</p>
          <dl className="mt-6 grid grid-cols-1 gap-x-8 gap-y-5 sm:grid-cols-2">
            {(featKeys.length ? featKeys : Object.keys(FEATURE_LABELS)).map((key) => (
              <div key={key} className="flex gap-3" data-testid={`glossary-${key}`}>
                <Check className="mt-0.5 h-4 w-4 shrink-0 text-accent" />
                <div>
                  <dt className="font-semibold text-secondary">{FEATURE_LABELS[key] ? FEATURE_LABELS[key][lang] : key}</dt>
                  <dd className="mt-0.5 text-sm text-slate-600">{FEATURE_DESCRIPTIONS[key] ? FEATURE_DESCRIPTIONS[key][lang] : ""}</dd>
                </div>
              </div>
            ))}
          </dl>
        </div>
      </div>
    </div>
  );
}
